<template>
    <VTextField
        ref="input"
        v-model="internalValue"
        v-bind="$attrs"
        :class="classList"
        :disabled="disabled"
        :rules="validationRules"
        outlined
        v-on="$listeners"
        @input="$emit('input', internalValue)"
    >
        <template #label>
            <SrInputLabel :label="label" :required="required" />
        </template>
        <template #append>
            <SrButton
                title="Open in new tab"
                text
                @click="openURLInNewTab"
                @mouseover="linkIconActive = true"
                @mouseleave="linkIconActive = false"
            >
                <SrIcon icon="search" size="s" :active="linkIconActive" />
            </SrButton>
        </template>
    </VTextField>
</template>

<script>
// https://vuetifyjs.com/en/components/text-fields#text-fields

import { USER_INPUT_CLASS } from '../../utils/classNames';
import { SrInputLabel } from '../SrInputLabel';
import SrIcon from '../SrIcon/SrIcon';
import SrButton from '../SrButton/SrButton';
import PropagateInputEvent from '../../mixins/PropagateInputEvent';
import { getInputRules, isRequiredRule, isHttpRule, isHttpsRule, noHttpProtocolRule } from '../../rules';

const ELEMENT_CLASS = 'sr-url-input';

/**
 * Standard input field with the option to open the given URL in a new tab.
 */
export default {
    name: 'SrURLInput',
    components: {
        SrButton,
        SrIcon,
        SrInputLabel,
    },
    mixins: [PropagateInputEvent],
    props: {
        /**
         * @model
         */
        value: {
            type: String,
            default: '',
        },
        /**
         * Input label
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Defines if the input is disabled.
         * Default: false
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Defines if the input has to be filled. Label will have a red asterisk (*) appended
         */
        required: {
            type: Boolean,
            default: false,
        },
        /**
         * Additional rules for validating user input
         */
        rules: {
            type: Array,
            default: () => [],
        },
        /**
         * Defines if the input URL has to be HTTPS protocol
         */
        secureUrl: {
            type: Boolean,
            default: false,
        },
        /**
         * Defines if HTTP(S) protocol needs to be specified
         */
        httpProtocolRequired: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            linkIconActive: false,
            internalValue: this.value,
        };
    },

    computed: {
        validationRules() {
            const rules = [...this.rules];

            if (!this.httpProtocolRequired) {
                rules.unshift((value) => (value ? noHttpProtocolRule(value) : true));
            } else if (this.secureUrl) {
                rules.unshift((value) => (value ? isHttpsRule(value) : true));
            } else {
                rules.unshift((value) => (value ? isHttpRule(value) : true));
            }

            return getInputRules(rules, this.required);
        },
        isValueNotEmpty() {
            return isRequiredRule(this.internalValue) === true;
        },
        isValid() {
            return this.$refs.input.validate();
        },
        classList() {
            return [ELEMENT_CLASS, USER_INPUT_CLASS];
        },
    },
    watch: {
        internalValue(value) {
            this.emitInput(value);
        },
        value(value) {
            this.internalValue = value;
        },
    },
    methods: {
        openURLInNewTab() {
            if (this.isValueNotEmpty && this.isValid) {
                window.open(this.internalValue, '_blank');
            }
        },
    },
};
</script>

<style lang="scss" scoped src="./_index.scss" />
